<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const validSender = helpers.withMessage(
  'Inserisci email@example.net o "Nome <email@example.net>"',
  value => {
    if (!value || value.trim() === '') return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const formattedEmailRegex = /^.+\s*<[^\s@]+@[^\s@]+\.[^\s@]+>$/;
    return emailRegex.test(value) || formattedEmailRegex.test(value);
  }
);

const validEmailList = helpers.withMessage(
  'Inserisci indirizzi email validi separati da virgola',
  value => {
    if (!value || value.trim() === '') return true;
    const emails = value
      .split(',')
      .map(e => e.trim())
      .filter(e => e);
    return emails.every(e => email.$validator(e));
  }
);

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['cancel', 'update:show'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: '',
      introText: '',
      cc: '',
      bcc: '',
      sender: 'Ideal Standard <info@idealstandard.it>',
      isSubmitting: false,
    };
  },
  validations: {
    email: {
      required,
      email,
      minLength: minLength(4),
    },
    cc: {
      validEmailList,
    },
    bcc: {
      validEmailList,
    },
    sender: {
      validSender,
    },
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    isFormValid() {
      return (
        !!this.email &&
        !this.v$.email.$error &&
        !this.v$.cc.$error &&
        !this.v$.bcc.$error &&
        !this.v$.sender.$error
      );
    },
  },
  methods: {
    onCancel() {
      this.$emit('cancel');
    },
    async onSubmit() {
      this.isSubmitting = true;
      try {
        await this.$store.dispatch('forwardConversation', {
          email: this.email,
          introText: this.introText,
          cc: this.cc,
          bcc: this.bcc,
          sender: this.sender,
          conversationId: this.currentChat.id,
        });
        useAlert('Conversazione inoltrata con successo');
        this.onCancel();
      } catch (error) {
        useAlert('Si è verificato un errore, riprova');
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Inoltra conversazione"
        header-content="Invia una copia della conversazione all’indirizzo email specificato"
      />
      <form class="w-full" @submit.prevent="onSubmit">
        <div class="w-full">
          <label :class="{ error: v$.email.$error }">
            Indirizzo email
            <input
              v-model="email"
              type="email"
              :placeholder="$t('EMAIL_TRANSCRIPT.FORM.EMAIL.PLACEHOLDER')"
              @input="v$.email.$touch"
            />
            <span v-if="v$.email.$error" class="message">
              {{ $t('EMAIL_TRANSCRIPT.FORM.EMAIL.ERROR') }}
            </span>
          </label>
          <label class="mt-4">
            Testo iniziale (opzionale)
            <textarea
              v-model="introText"
              rows="4"
              placeholder="Testo da visualizzare prima della conversazione inoltrata"
            />
          </label>
          <div class="flex gap-4 mt-4">
            <label class="flex-1" :class="{ error: v$.cc.$error }">
              CC (opzionale)
              <input
                v-model="cc"
                type="text"
                placeholder="Inserisci indirizzi email"
                @input="v$.cc.$touch"
              />
              <span v-if="v$.cc.$error" class="message">
                Inserisci indirizzi email validi
              </span>
            </label>
            <label class="flex-1" :class="{ error: v$.bcc.$error }">
              CCN (opzionale)
              <input
                v-model="bcc"
                type="text"
                placeholder="Inserisci indirizzi email"
                @input="v$.bcc.$touch"
              />
              <span v-if="v$.bcc.$error" class="message">
                Inserisci indirizzi email validi
              </span>
            </label>
          </div>
          <label :class="{ error: v$.sender.$error }">
            Mittente (opzionale)
            <input
              v-model="sender"
              type="text"
              :placeholder="$t('EMAIL_TRANSCRIPT.FORM.EMAIL.PLACEHOLDER')"
              @input="v$.sender.$touch"
            />
            <span v-if="v$.sender.$error" class="message">
              Inserisci "email@address.com" o "Mittente
              &lt;email@address.com&gt;"
            </span>
          </label>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('EMAIL_TRANSCRIPT.CANCEL')"
            @click.prevent="onCancel"
          />
          <NextButton type="submit" label="Inoltra" :disabled="!isFormValid" />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
